import { useEffect, useState } from 'react';

import { App, Form, Input, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { OperationRequest } from 'models/OperationRequest';

import { OperationRequirementService } from 'services/OperationRequirementService';

interface CancelRequestModalProps {
  request: OperationRequest | null;
  onCancel: () => void;
  onCancelled: () => void;
}

const REASON_KEYS = [
  'client_cancelled',
  'date_change',
  'load_error',
  'budget',
  'other',
] as const;

function CancelRequestModal({
  request,
  onCancel,
  onCancelled,
}: CancelRequestModalProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const i18nBase =
    'scenes.requirements_operation.components.cancel_request_modal';

  const reason = Form.useWatch('reason', form);
  const needsDetail = reason === 'other';

  useEffect(() => {
    if (request) {
      form.resetFields();
    }
  }, [request, form]);

  const handleOk = async () => {
    if (!request) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const response = await OperationRequirementService.cancel(request.id, {
        confirmCode: values.confirmCode,
        reason: t(`${i18nBase}.reasons.${values.reason}`),
        detail: values.detail,
      });
      setSubmitting(false);

      if (response.success) {
        message.success(t(`${i18nBase}.messages.success`), 3.5);
        onCancelled();
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.messages.error`),
          3.5
        );
      }
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={!!request}
      title={t(`${i18nBase}.title`)}
      okText={t(`${i18nBase}.buttons.confirm`)}
      cancelText={t(`${i18nBase}.buttons.cancel`)}
      okButtonProps={{ danger: true, loading: submitting }}
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
    >
      {request && (
        <>
          <p>
            {t(`${i18nBase}.description`, {
              slots: request.slots,
              brand: request.brand,
              campaign: request.campaign,
            })}
            {request.filled > 0
              ? ` ${t(`${i18nBase}.description_assigned`, {
                  filled: request.filled,
                })}`
              : ''}
          </p>

          <Form form={form} layout="vertical" requiredMark>
            <Form.Item
              name="reason"
              label={t(`${i18nBase}.labels.reason`)}
              rules={[
                {
                  required: true,
                  message: t(`${i18nBase}.validations.reason_required`),
                },
              ]}
            >
              <Select
                placeholder={t(`${i18nBase}.placeholders.reason`)}
                options={REASON_KEYS.map((key) => ({
                  value: key,
                  label: t(`${i18nBase}.reasons.${key}`),
                }))}
              />
            </Form.Item>

            {needsDetail && (
              <Form.Item
                name="detail"
                label={t(`${i18nBase}.labels.detail`)}
                rules={[
                  {
                    required: true,
                    min: 4,
                    message: t(`${i18nBase}.validations.detail_required`),
                  },
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={t(`${i18nBase}.placeholders.detail`)}
                />
              </Form.Item>
            )}

            <Form.Item
              name="confirmCode"
              label={t(`${i18nBase}.labels.confirm_code`, { id: request.id })}
              rules={[
                {
                  required: true,
                  message: t(`${i18nBase}.validations.confirm_required`),
                },
                {
                  validator: async (_, value) => {
                    if (
                      !value ||
                      value.trim().toUpperCase() !== request.id.toUpperCase()
                    ) {
                      throw new Error(
                        t(`${i18nBase}.validations.confirm_mismatch`)
                      );
                    }
                  },
                },
              ]}
            >
              <Input placeholder={request.id} autoComplete="off" />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}

export default CancelRequestModal;
